import { useState, useEffect } from "react";
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import WallpaperOutlinedIcon from '@mui/icons-material/WallpaperOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from "../../AuthContext";

const Item = ({ title, to, icon, selected, setSelected }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    
    // Check if this item should be active based on current path
    const isActive = location.pathname === to || selected === title;

    return (
        <MenuItem
            active={isActive}
            style={{
                color: isActive ? colors.blue[400] : colors.white[700],
                backgroundColor: isActive 
                    ? colors.white[500] 
                    : isHovered 
                        ? colors.white[500] 
                        : 'transparent',
                transition: 'background-color 0.1s ease',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => setSelected(title)}
            icon={icon}
            component={<Link to={to} />}
        >
            <Typography>{title}</Typography>
        </MenuItem>
    );
};

const WebsiteSideBar = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [selected, setSelected] = useState("Dashboard");
    const { userRole } = useAuth();
    const location = useLocation();

    // Update selected state based on current path
    useEffect(() => {
        const path = location.pathname;
        
        if (path === "/website/dashboard") setSelected("Dashboard");
        else if (path === "/website/dashboard/settings") setSelected("Company Details");
        else if (path === "/website/dashboard/announcements") setSelected("Top header Adss");
        else if (path === "/website/dashboard/special") setSelected("Special");
        else if (path === "/website/dashboard/public-holidays") setSelected("Public Holidays");
        else if (path === "/website/dashboard/backgrounds") setSelected("Backgrounds");
        else if (path === "/website/dashboard/about-us") setSelected("About Us");
        else if (path === "/website/dashboard/departments") setSelected("Departments");
        else if (path === "/website/dashboard/services") setSelected("Services");
        else if (path === "/website/dashboard/projects") setSelected("Projects");
        else if (path === "/website/dashboard/modules") setSelected("Modules");
        else if (path === "/website/dashboard/why-steam") setSelected("Why Steam (STATS)");
        else if (path === "/website/dashboard/events") setSelected("Events");
        else if (path === "/website/dashboard/news") setSelected("News");
        else if (path === "/website/dashboard/clients") setSelected("Our Clients");
        else if (path === "/website/dashboard/csr") setSelected("CSR Projects");
        else if (path === "/website/dashboard/csr-activity") setSelected("CSR Activities");
        else if (path === "/website/dashboard/blog") setSelected("Our Blogs");
        else if (path === "/website/dashboard/adverts") setSelected("Adverts");
        else if (path === "/website/dashboard/newsletter") setSelected("News Letters");
        else if (path === "/website/dashboard/gallery") setSelected("Our Gallery");
        else if (path === "/website/dashboard/testimonials") setSelected("Testimonials");
        else if (path === "/website/dashboard/partners") setSelected("Our Partners");
    }, [location.pathname]);

    return (
        <Box>
            <Sidebar 
                collapsed={isCollapsed}
                backgroundColor={colors.dark[800]}
                rootStyles={{
                    background: colors.dark[900],
                    height: "100vh",
                    padding: "0"
                }}
            >
                <Menu iconShape="square">
                    <MenuItem
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
                        style={{
                            margin: "10px 0 20px 0",
                            color: colors.dark[400],
                            background: colors.dark[800]
                        }}
                    >
                        {!isCollapsed && (
                            <Box 
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                ml="15px"
                            >
                                <Typography variant="h3" color={colors.primary[100]}>
                                    {userRole === 'admin' ? 'WEBSITE ADMIN' : 'WEBSITE'}
                                </Typography>
                                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                                    <MenuOutlinedIcon />
                                </IconButton>
                            </Box>
                        )}
                    </MenuItem>
                    
                    {userRole !== 'receptionist' && (
                        <Box paddingLeft={isCollapsed ? undefined : "10%"} sx={{
                            padding: '1rem 0 1rem 2rem',
                            display: 'flex',
                            gap: '1rem',
                            alignItems: 'center',
                            justifyContent: 'start'
                        }}>
                            <IconButton 
                                onClick={() => window.history.back()}
                                sx={{ 
                                bgcolor: colors.white[500],
                                color: colors.green[500],
                                '&:hover': { bgcolor: colors.primary[400] }
                                }}
                                selected={selected}
                                setSelected={setSelected}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography>Go Back</Typography>
                        </Box>
                    )}
                    
                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        <Item
                            title="Dashboard"
                            to="/website/dashboard"
                            icon={<HomeOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Box>
                    
                    <Typography
                        variant="h6"
                        color={colors.blue[400]}
                        sx={{m:"15px 0 5px 20px"}}
                    >
                        Website Content
                    </Typography>

                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        <Item
                            title="Company Details"
                            to="/website/dashboard/settings"  // ✅ Already correct
                            icon={<BusinessOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Top header Ads"
                            to="/website/dashboard/announcements"  // ✅ Already correct
                            icon={<AnnouncementOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />

                        <Item
                            title="Special"
                            to="/website/dashboard/special"  // ✅ Already correct
                            icon={<AnnouncementOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Public Holidays"
                            to="/website/dashboard/public-holidays"  // ✅ Already correct
                            icon={<CalendarTodayOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Backgrounds"
                            to="/website/dashboard/backgrounds"  // ✅ Changed from /admin/dashboard/website/backgrounds
                            icon={<WallpaperOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="About Us"
                            to="/website/dashboard/about-us"  // ✅ Changed from /admin/dashboard/website/about-us
                            icon={<InfoOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Departments"
                            to="/website/dashboard/departments"  // ✅ Changed from /admin/dashboard/website/departments
                            icon={<AccountTreeOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Services"
                            to="/website/dashboard/services"  // ✅ Changed from /admin/dashboard/website/services
                            icon={<BuildOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Projects"
                            to="/website/dashboard/projects"  // ✅ Changed from /admin/dashboard/website/projects
                            icon={<AssignmentOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Modules"
                            to="/website/dashboard/modules"  // ✅ Changed from /admin/dashboard/website/modules
                            icon={<ExtensionOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Why Steam (STATS)"
                            to="/website/dashboard/why-steam"  // ✅ Changed from /admin/dashboard/website/why-steam
                            icon={<TrendingUpOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Events"
                            to="/website/dashboard/events"  // ✅ Changed from /admin/dashboard/website/events
                            icon={<EventOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="News"
                            to="/website/dashboard/news"  // ✅ Changed from /admin/dashboard/website/news
                            icon={<ArticleOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Our Clients"
                            to="/website/dashboard/clients"  // ✅ Changed from /admin/dashboard/website/clients
                            icon={<GroupOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="CSR Projects"
                            to="/website/dashboard/csr"  // ✅ Changed from /admin/dashboard/website/csr
                            icon={<HandshakeOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="CSR Activities"
                            to="/website/dashboard/csr-activity"  // ✅ Changed from /admin/dashboard/website/csr-activity
                            icon={<EmojiObjectsOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="STEAM Project"
                            to="/website/dashboard/steam/project"  // ✅ Changed from /admin/dashboard/website/csr-activity
                            icon={<AssignmentOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Our Blogs"
                            to="/website/dashboard/blog"  // ✅ Changed from /admin/dashboard/website/blog
                            icon={<CampaignOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Adverts"
                            to="/website/dashboard/adverts"  // ✅ Changed from /admin/dashboard/website/adverts
                            icon={<CampaignOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="News Letters"
                            to="/website/dashboard/newsletter"  // ✅ Changed from /admin/dashboard/website/newsletter
                            icon={<EmailOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Our Gallery"
                            to="/website/dashboard/gallery"  // ✅ Changed from /admin/dashboard/website/gallery
                            icon={<PhotoLibraryOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Testimonials"
                            to="/website/dashboard/testimonials"  // ✅ Changed from /admin/dashboard/website/testimonials
                            icon={<RateReviewOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Our Partners"
                            to="/website/dashboard/partners"  // ✅ Changed from /admin/dashboard/website/partners
                            icon={<PeopleAltOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Box>
                </Menu>
            </Sidebar>
        </Box>
    );
}

export default WebsiteSideBar;